// src/components/AdminPage.js
import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

function AdminPage({ token, onLogout }) {
  // 活動列表與載入狀態
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  // 黑名單／違規紀錄
  const [blackListRecords, setBlackListRecords] = useState([]);
  const [blacklistLoading, setBlacklistLoading] = useState(false);

  // 違規登記欄位
  const [violationStudentId, setViolationStudentId] = useState('');
  const [violationName, setViolationName] = useState('');
  const [violationReason, setViolationReason] = useState('');

  // 新增活動欄位（只用於新增）
  const [addFields, setAddFields] = useState({
    name: '',
    date: '',
    startTime: '',
    endTime: '',
    maxCapacity: ''
  });

  // 編輯活動（使用 Modal）
  const [editModalShow, setEditModalShow] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editFields, setEditFields] = useState({
    name: '',
    date: '',
    startTime: '',
    endTime: '',
    maxCapacity: ''
  });

  useEffect(() => {
    fetchSummary();
    fetchBlacklistRecords();
  }, []);

  // 取得活動清單／報表
  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/reports/summary', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSummary(data);
    } catch (err) {
      console.error('抓取活動報表失敗:', err);
    } finally {
      setLoading(false);
    }
  };

  // 取得黑名單／違規紀錄
  const fetchBlacklistRecords = async () => {
    setBlacklistLoading(true);
    try {
      const res = await fetch('/api/blacklist', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setBlackListRecords(data);
    } catch (err) {
      console.error('抓取黑名單失敗:', err);
    } finally {
      setBlacklistLoading(false);
    }
  };

  // 新增活動
  const handleAddEvent = async (e) => {
    e.preventDefault();
    const payload = {
      ...addFields,
      maxCapacity: Number(addFields.maxCapacity)
    };
    try {
      const res = await fetch('/api/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok) {
        alert('新增活動成功');
        // 清空表單並重新抓取活動
        setAddFields({
          name: '',
          date: '',
          startTime: '',
          endTime: '',
          maxCapacity: ''
        });
        fetchSummary();
      } else {
        alert(data.message || '新增活動失敗');
      }
    } catch (error) {
      console.error('新增活動錯誤:', error);
    }
  };

  // 開啟編輯 Modal，帶入活動資料
  const openEditModal = (evt) => {
    setEditItem(evt);
    setEditFields({
      name: evt.name,
      date: evt.date,
      startTime: evt.startTime,
      endTime: evt.endTime,
      maxCapacity: evt.maxCapacity,
    });
    setEditModalShow(true);
  };

  // 編輯活動送出
  const handleEditSubmit = async () => {
    if (!editItem) return;
    try {
      const res = await fetch(`/api/events/${editItem.eventId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editFields),
      });
      const data = await res.json();
      if (res.ok) {
        alert('修改成功');
        setEditModalShow(false);
        fetchSummary();
      } else {
        alert(data.message || '修改失敗');
      }
    } catch (err) {
      console.error('修改活動發生錯誤:', err);
    }
  };

  // 刪除活動
  const handleDelete = async (eventId) => {
    if (!window.confirm('確認要刪除此活動？')) return;
    try {
      const res = await fetch(`/api/events/${eventId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        alert(data.message);
        // 刪除後重新抓取活動列表
        fetchSummary();
      } else {
        alert(data.message || '刪除活動失敗');
      }
    } catch (err) {
      console.error('刪除活動錯誤:', err);
    }
  };

  // 匯出活動預約清單
  const handleExport = async (eventId) => {
    try {
      const res = await fetch(`/api/events/${eventId}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('匯出失敗');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `event-${eventId}-reservation.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('匯出錯誤:', error);
      alert('匯出錯誤');
    }
  };

  // 匯出整份活動報表
  const handleExportAll = async () => {
    try {
      const res = await fetch('/api/reports/export', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('匯出失敗');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'event-summary.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('總覽報表匯出錯誤:', err);
      alert('匯出錯誤');
    }
  };

  // 登記違規
  const handleRecordViolation = async () => {
    try {
      const res = await fetch('/api/blacklist/recordViolation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          studentId: violationStudentId,
          name: violationName,
          reason: violationReason,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        alert(data.message);
        setViolationStudentId('');
        setViolationName('');
        setViolationReason('');
        fetchBlacklistRecords();
      } else {
        alert(data.message || '登記違規失敗');
      }
    } catch (err) {
      console.error('登記違規錯誤:', err);
    }
  };

  // 刪除違規紀錄
  const handleDeleteViolation = async (recordId) => {
    if (!window.confirm('確定要刪除此違規紀錄？')) return;
    try {
      const res = await fetch(`/api/blacklist/${recordId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        alert(data.message);
        fetchBlacklistRecords();
      } else {
        alert(data.message || '刪除違規紀錄失敗');
      }
    } catch (error) {
      console.error('刪除違規紀錄錯誤:', error);
    }
  };

  if (loading) {
    return <p className="text-center">活動報表載入中...</p>;
  }

  return (
    <>
      <div className="container mt-4">
        <h1 className="mb-4">後台管理</h1>

        {/* 匯出總覽按鈕 */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">活動報表</h4>
          <button className="btn btn-outline-primary" onClick={handleExportAll}>
            匯出總覽報表
          </button>
        </div>

        {/* 新增活動表單 */}
        <div className="card p-3 mb-4">
          <h4>新增活動</h4>
          <form onSubmit={handleAddEvent} className="row g-2">
            <div className="col-md-3">
              <input
                className="form-control"
                value={addFields.name}
                onChange={(e) => setAddFields({ ...addFields, name: e.target.value })}
                placeholder="活動名稱"
                required
              />
            </div>
            <div className="col-md-2">
              <input
                className="form-control"
                type="date"
                value={addFields.date}
                onChange={(e) => setAddFields({ ...addFields, date: e.target.value })}
                required
              />
            </div>
            <div className="col-md-2">
              <input
                className="form-control"
                type="time"
                value={addFields.startTime}
                onChange={(e) => setAddFields({ ...addFields, startTime: e.target.value })}
                required
              />
            </div>
            <div className="col-md-2">
              <input
                className="form-control"
                type="time"
                value={addFields.endTime}
                onChange={(e) => setAddFields({ ...addFields, endTime: e.target.value })}
                required
              />
            </div>
            <div className="col-md-2">
              <input
                className="form-control"
                type="number"
                value={addFields.maxCapacity}
                onChange={(e) => setAddFields({ ...addFields, maxCapacity: e.target.value })}
                placeholder="人數上限"
                required
              />
            </div>
            <div className="col-md-1">
              <button className="btn btn-success w-100" type="submit">
                新增
              </button>
            </div>
          </form>
        </div>

        {/* 違規管理 */}
        <div className="card p-3 mb-4">
          <h4>違規管理</h4>
          <div className="row g-2 align-items-center">
            <div className="col-md-3">
              <input
                className="form-control"
                placeholder="學號"
                value={violationStudentId}
                onChange={(e) => setViolationStudentId(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <input
                className="form-control"
                placeholder="姓名"
                value={violationName}
                onChange={(e) => setViolationName(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <input
                className="form-control"
                placeholder="違規原因"
                value={violationReason}
                onChange={(e) => setViolationReason(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <button className="btn btn-danger w-100" onClick={handleRecordViolation}>
                登記違規
              </button>
            </div>
          </div>
        </div>

        {/* 登出按鈕 */}
        <button className="btn btn-outline-danger mb-4" onClick={onLogout}>
          登出
        </button>

        {/* 活動報表 */}
        <h4 className="mb-3">活動報表</h4>
        <table className="table table-striped table-bordered">
          <thead>
            <tr>
              <th>活動名稱</th>
              <th>日期</th>
              <th>時間</th>
              <th>已預約</th>
              <th>剩餘名額</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {summary.map((evt) => (
              <tr key={evt.eventId}>
                <td>{evt.name}</td>
                <td>{evt.date}</td>
                <td>
                  {evt.startTime} - {evt.endTime}
                </td>
                <td>{evt.reservedCount}</td>
                <td>{evt.availableSpots}</td>
                <td>
                  <button
                    className="btn btn-outline-info btn-sm me-1"
                    onClick={() => handleExport(evt.eventId)}
                  >
                    匯出
                  </button>
                  <button
                    className="btn btn-outline-warning btn-sm me-1"
                    onClick={() => openEditModal(evt)}
                  >
                    修改
                  </button>
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => handleDelete(evt.eventId)}
                  >
                    刪除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* 黑名單／違規紀錄 */}
        <h4 className="mt-5">違規紀錄／黑名單</h4>
        {blacklistLoading ? (
          <p>載入中...</p>
        ) : (
          <table className="table table-striped table-bordered mt-3">
            <thead>
              <tr>
                <th>學生ID</th>
                <th>姓名</th>
                <th>Email</th>
                <th>違規次數</th>
                <th>黑名單</th>
                <th>解鎖時間</th>
                <th>原因</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {blackListRecords.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center">
                    目前沒有違規紀錄 / 黑名單
                  </td>
                </tr>
              ) : (
                blackListRecords.map((item) => (
                  <tr key={item.id}>
                    <td>{item.User.studentId}</td>
                    <td>{item.User.name}</td>
                    <td>{item.User.email}</td>
                    <td>{item.User.violationCount}</td>
                    <td>{item.User.isBlacklisted ? '是' : '否'}</td>
                    <td>
                      {item.User.blacklistUntil
                        ? dayjs(item.User.blacklistUntil).format('YYYY/MM/DD HH:mm')
                        : '—'}
                    </td>
                    <td>{item.reason || '—'}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteViolation(item.id)}
                      >
                        刪除
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* 編輯活動 Modal */}
      <Modal show={editModalShow} onHide={() => setEditModalShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>編輯活動</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-label">活動名稱</label>
          <input
            className="form-control mb-2"
            value={editFields.name}
            onChange={(e) => setEditFields({ ...editFields, name: e.target.value })}
          />
          <label className="form-label">日期</label>
          <input
            type="date"
            className="form-control mb-2"
            value={editFields.date}
            onChange={(e) => setEditFields({ ...editFields, date: e.target.value })}
          />
          <label className="form-label">開始時間</label>
          <input
            type="time"
            className="form-control mb-2"
            value={editFields.startTime}
            onChange={(e) => setEditFields({ ...editFields, startTime: e.target.value })}
          />
          <label className="form-label">結束時間</label>
          <input
            type="time"
            className="form-control mb-2"
            value={editFields.endTime}
            onChange={(e) => setEditFields({ ...editFields, endTime: e.target.value })}
          />
          <label className="form-label">人數上限</label>
          <input
            type="number"
            className="form-control"
            value={editFields.maxCapacity}
            onChange={(e) => setEditFields({ ...editFields, maxCapacity: e.target.value })}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditModalShow(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleEditSubmit}>
            儲存修改
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default AdminPage;
